import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SchemaOrgJsonLd from '@/components/feature/SchemaOrgJsonLd';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://superbuyspread.space';
const GUIDE_URL = `${SITE_URL}/guide/superbuy-spreadsheet-101`;

const guideSchemas = [
  {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Superbuy Spreadsheet 101: How to Find Hidden Gems and Build Your Ultimate Shopping Haul in 2025',
    description: 'A comprehensive beginner\'s guide to Superbuy Spreadsheets. Learn how to find verified product links, read QC photos, compare sellers across 14 categories, and build your best international shopping haul.',
    author: {
      '@type': 'Organization',
      name: 'Superbuy Spreadsheet Hub',
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Superbuy Spreadsheet Hub',
      url: SITE_URL,
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67defc9f4dfae2c04004e8ea78b88107.png',
      },
    },
    datePublished: '2025-07-01',
    dateModified: '2025-07-12',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': GUIDE_URL,
    },
    image: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20flatlay%20photography%20with%20cream%20white%20background%2C%20laptop%20and%20notebook%20with%20fashion%20accessories%20scattered%20artistically%2C%20warm%20natural%20lighting%2C%20clean%20modern%20composition%2C%20soft%20shadows%2C%20sage%20green%20and%20peach%20accents%2C%20editorial%20aesthetic&width=1800&height=600&seq=article-hero-spreadsheet-101&orientation=landscape',
    wordCount: 3500,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: SITE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Guide',
        item: GUIDE_URL,
      },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Is the Superbuy Spreadsheet safe to use?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Absolutely. Every link in the spreadsheet has been vetted by experienced buyers. The spreadsheet itself does not handle any transactions — you purchase through your chosen agent, which adds an extra layer of security.',
        },
      },
      {
        '@type': 'Question',
        name: 'How much can I save using a spreadsheet?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Savings vary by category, but most spreadsheet users report saving 40-70% compared to buying similar items from domestic retailers. Shoes and Hoodies typically offer the best value.',
        },
      },
      {
        '@type': 'Question',
        name: 'Do I need to speak Chinese to use the spreadsheet?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Not at all! The spreadsheet is organized in English, and shopping agents like Superbuy provide fully English-language interfaces. The entire experience is designed for international shoppers.',
        },
      },
      {
        '@type': 'Question',
        name: 'How do I know which size to order?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Most spreadsheet entries include sizing notes from previous buyers. QC photos with measurements are your best friend for confirming sizes before shipping.',
        },
      },
      {
        '@type': 'Question',
        name: 'What if an item arrives damaged or incorrect?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'If you spot an issue during the QC stage, you can request a return or exchange before international shipping. Even after shipping, most agents offer some form of insurance or compensation.',
        },
      },
    ],
  },
];

export default function SuperbuySpreadsheetGuide() {
  const tableOfContents = [
    { id: 'introduction', title: 'Introduction: Why Spreadsheets Are Changing Shopping' },
    { id: 'what-is', title: 'What Exactly Is a Superbuy Spreadsheet?' },
    { id: 'why-use', title: 'Why Thousands of Shoppers Use Spreadsheets' },
    { id: 'getting-started', title: 'Getting Started: Your First Superbuy Spreadsheet' },
    { id: 'categories', title: 'Understanding the 14 Core Categories' },
    { id: 'qc-photos', title: 'How to Read QC Photos Like a Pro' },
    { id: 'comparison', title: 'Spreadsheet Shopping vs. Manual Searching' },
    { id: 'tips', title: '10 Pro Tips for Spreadsheet Success' },
    { id: 'shipping', title: 'Shipping and Agent Guide' },
    { id: 'faq', title: 'Frequently Asked Questions' },
  ];

  return (
    <>
      {guideSchemas.map((schema, idx) => (
        <SchemaOrgJsonLd key={idx} schema={schema} />
      ))}
      <Navbar />

      <main className="pt-16">
        {/* Article Hero */}
        <section className="relative min-h-[380px] md:min-h-[450px] flex items-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20flatlay%20photography%20with%20cream%20white%20background%2C%20laptop%20and%20notebook%20with%20fashion%20accessories%20scattered%20artistically%2C%20warm%20natural%20lighting%2C%20clean%20modern%20composition%2C%20soft%20shadows%2C%20sage%20green%20and%20peach%20accents%2C%20editorial%20aesthetic&width=1800&height=600&seq=article-hero-spreadsheet-101&orientation=landscape"
              alt="Superbuy Spreadsheet 101 — Beginner's guide to finding hidden gems using spreadsheets"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-900/40 via-foreground-900/20 to-background-50"></div>
          </div>

          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 pt-20 pb-10">
            <div className="flex items-center gap-3 text-sm text-background-50/80 mb-4">
              <Link to="/" className="hover:text-background-50 transition-colors">Home</Link>
              <span>/</span>
              <span className="text-background-50">Guide</span>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight mb-4 max-w-3xl">
              Superbuy Spreadsheet 101: How to Find Hidden Gems and Build Your Ultimate Shopping Haul in 2025
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-background-50/70">
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                12 min read
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                Updated July 2025
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                By Spreadsheet Experts
              </span>
            </div>
          </div>
        </section>

        {/* Article Body */}
        <section className="py-10 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
              {/* Table of Contents - Sidebar */}
              <aside className="lg:w-[260px] flex-shrink-0">
                <div className="lg:sticky lg:top-24 bg-background-100 rounded-xl border border-background-200/60 p-5">
                  <h4 className="font-heading font-semibold text-sm text-foreground-900 mb-3 flex items-center gap-2">
                    <i className="ri-list-unordered text-accent-500"></i>
                    Table of Contents
                  </h4>
                  <nav className="space-y-1">
                    {tableOfContents.map((item) => (
                      <a
                        key={item.id}
                        href={`#${item.id}`}
                        className="block text-xs text-foreground-600 hover:text-accent-600 transition-colors py-1.5 leading-snug"
                      >
                        {item.title}
                      </a>
                    ))}
                  </nav>
                </div>
              </aside>

              {/* Main Content */}
              <article className="flex-1 min-w-0">
                <div className="space-y-8 text-foreground-700 leading-relaxed">
                  {/* Quick Answer Box */}
                  <div className="bg-accent-50 border border-accent-200 rounded-xl p-5 md:p-6">
                    <h3 className="font-heading font-semibold text-accent-800 mb-2 text-sm flex items-center gap-2">
                      <i className="ri-lightbulb-flash-line"></i>
                      Quick Answer
                    </h3>
                    <p className="text-sm text-accent-900 leading-relaxed">
                      A Superbuy Spreadsheet is a curated document containing thousands of verified product links from platforms like Taobao, Weidian, and 1688. It saves shoppers hours of searching, provides real QC photos, and organizes finds into categories like Shoes, Hoodies, Jackets, Bags, and Accessories. Whether you are a complete beginner or an experienced hauler, spreadsheets are the fastest way to discover quality products at competitive prices.
                    </p>
                  </div>

                  {/* Introduction */}
                  <section id="introduction">
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                      Introduction: Why Spreadsheets Are Changing the Way People Shop
                    </h2>
                    <p className="text-sm md:text-base mb-4">
                      International online shopping has exploded in popularity over the past few years, and for good reason. Platforms like Taobao, Weidian, and 1688 offer an incredible variety of products at prices that are often a fraction of what you would pay from domestic retailers. But here is the problem: finding quality items among millions of listings is exhausting, time-consuming, and risky — especially if you do not speak the language or understand the platform.
                    </p>
                    <p className="text-sm md:text-base mb-4">
                      This is where <strong className="text-foreground-900">Superbuy Spreadsheets</strong> come in. Think of a spreadsheet as your personal shopping assistant — a meticulously organized, community-curated list of verified product links, complete with real QC (Quality Check) photos, seller ratings, and buying tips. Instead of spending three hours scrolling through Weidian at 2 AM, you can open a spreadsheet and instantly access hundreds of pre-vetted finds across every category imaginable.
                    </p>
                    <p className="text-sm md:text-base">
                      In this comprehensive guide, we will walk you through everything you need to know about Superbuy Spreadsheets — from what they are and how they work, to advanced tips for finding the best hidden gems. By the end, you will be equipped to build your best haul yet. And if you want to dive straight into the latest finds, head over to{' '}
                      <Link to="/" className="text-accent-600 hover:text-accent-700 underline font-medium">the Superbuy Spreadsheet Hub</Link>
                      {' '}for daily updated links across all 14 categories.
                    </p>
                  </section>

                  {/* Key Takeaways */}
                  <div className="bg-secondary-50 border border-secondary-200 rounded-xl p-5 md:p-6">
                    <h3 className="font-heading font-semibold text-secondary-800 mb-3 text-sm">Key Takeaways</h3>
                    <ul className="space-y-2 text-sm text-secondary-900">
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-secondary-500 mt-0.5 flex-shrink-0"></i>
                        <span>Superbuy Spreadsheets organize thousands of verified product links by category and brand</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-secondary-500 mt-0.5 flex-shrink-0"></i>
                        <span>Real QC photos help you verify quality before purchasing — no more gambling on product images</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-secondary-500 mt-0.5 flex-shrink-0"></i>
                        <span>The spreadsheet covers 14 categories from Shoes and Hoodies to Bags, Belts, and Perfumes</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-secondary-500 mt-0.5 flex-shrink-0"></i>
                        <span>Using an agent like Superbuy simplifies purchasing, inspection, and international shipping</span>
                      </li>
                    </ul>
                  </div>

                  {/* What Is */}
                  <section id="what-is">
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                      What Exactly Is a Superbuy Spreadsheet?
                    </h2>
                    <p className="text-sm md:text-base mb-4">
                      At its core, a Superbuy Spreadsheet is a shared document — typically hosted on Google Sheets or a dedicated website — that compiles product links from Chinese e-commerce platforms. But calling it just a &ldquo;list of links&rdquo; would be like calling a Michelin-starred restaurant &ldquo;a place that serves food.&rdquo; The best spreadsheets are living documents maintained by dedicated communities of experienced international shoppers.
                    </p>
                    <p className="text-sm md:text-base mb-4">
                      Each entry in a quality spreadsheet contains much more than a URL. You will typically find the brand name, a detailed product description, the price range (often in both CNY and USD), direct links to the product page, real QC photos taken by previous buyers, seller ratings, and community notes about sizing, quality, and shipping experiences. Some advanced spreadsheets even include comparison tables so you can see how similar items from different sellers stack up against each other.
                    </p>
                    <p className="text-sm md:text-base mb-4">
                      The &ldquo;Superbuy&rdquo; part of the name refers to Superbuy, one of the most popular shopping agents used by international buyers. An agent acts as your middleman — they purchase items on your behalf, receive them at their warehouse, take QC photos so you can inspect the quality, and then ship everything to you in one consolidated package. The spreadsheet is essentially the discovery layer, while the agent handles the logistics.
                    </p>
                    <div className="bg-background-100 rounded-xl border border-background-200/60 p-5 my-6">
                      <h4 className="font-heading font-semibold text-foreground-900 mb-3 text-sm">How a Spreadsheet Entry Typically Looks:</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs md:text-sm">
                          <thead>
                            <tr className="border-b border-background-200">
                              <th className="text-left py-2 px-3 font-semibold text-foreground-800">Brand</th>
                              <th className="text-left py-2 px-3 font-semibold text-foreground-800">Item</th>
                              <th className="text-left py-2 px-3 font-semibold text-foreground-800">Price</th>
                              <th className="text-left py-2 px-3 font-semibold text-foreground-800">QC</th>
                              <th className="text-left py-2 px-3 font-semibold text-foreground-800">Rating</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-b border-background-200/60">
                              <td className="py-2 px-3 text-foreground-700">Nike</td>
                              <td className="py-2 px-3 text-foreground-700">Air Force 1 White</td>
                              <td className="py-2 px-3 text-accent-700 font-medium">$28</td>
                              <td className="py-2 px-3 text-accent-600">✅ Available</td>
                              <td className="py-2 px-3 text-foreground-700">⭐⭐⭐⭐⭐</td>
                            </tr>
                            <tr className="border-b border-background-200/60">
                              <td className="py-2 px-3 text-foreground-700">Stussy</td>
                              <td className="py-2 px-3 text-foreground-700">Dice Knit Sweater</td>
                              <td className="py-2 px-3 text-accent-700 font-medium">$32</td>
                              <td className="py-2 px-3 text-accent-600">✅ Available</td>
                              <td className="py-2 px-3 text-foreground-700">⭐⭐⭐⭐</td>
                            </tr>
                            <tr>
                              <td className="py-2 px-3 text-foreground-700">Carhartt</td>
                              <td className="py-2 px-3 text-foreground-700">Cargo Pants Olive</td>
                              <td className="py-2 px-3 text-accent-700 font-medium">$22</td>
                              <td className="py-2 px-3 text-accent-600">✅ Available</td>
                              <td className="py-2 px-3 text-foreground-700">⭐⭐⭐⭐⭐</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </section>

                  {/* Why Use */}
                  <section id="why-use">
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                      Why Thousands of Shoppers Swear by Spreadsheets
                    </h2>
                    <p className="text-sm md:text-base mb-4">
                      If you have ever tried shopping on Taobao or Weidian without a guide, you know the struggle. The interfaces are in Chinese, product photos can be misleading, and it is nearly impossible to tell a quality seller from a scammer without experience. A spreadsheet eliminates all of these pain points in one fell swoop.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
                      <div className="bg-background-100 rounded-xl border border-background-200/60 p-5">
                        <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center mb-3">
                          <i className="ri-time-line text-accent-600"></i>
                        </div>
                        <h4 className="font-heading font-semibold text-foreground-900 mb-1">Time Saved</h4>
                        <p className="text-sm text-foreground-600">Reduce hours of searching to minutes of browsing. The average spreadsheet user saves 4-6 hours per shopping session.</p>
                      </div>
                      <div className="bg-background-100 rounded-xl border border-background-200/60 p-5">
                        <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center mb-3">
                          <i className="ri-shield-check-line text-accent-600"></i>
                        </div>
                        <h4 className="font-heading font-semibold text-foreground-900 mb-1">Risk Reduction</h4>
                        <p className="text-sm text-foreground-600">Every link is vetted. QC photos let you see exactly what you are buying before it ships internationally.</p>
                      </div>
                      <div className="bg-background-100 rounded-xl border border-background-200/60 p-5">
                        <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center mb-3">
                          <i className="ri-money-dollar-circle-line text-accent-600"></i>
                        </div>
                        <h4 className="font-heading font-semibold text-foreground-900 mb-1">Price Comparison</h4>
                        <p className="text-sm text-foreground-600">Compare prices across sellers instantly. Avoid overpaying for identical items listed at different prices.</p>
                      </div>
                      <div className="bg-background-100 rounded-xl border border-background-200/60 p-5">
                        <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center mb-3">
                          <i className="ri-group-line text-accent-600"></i>
                        </div>
                        <h4 className="font-heading font-semibold text-foreground-900 mb-1">Community Wisdom</h4>
                        <p className="text-sm text-foreground-600">Benefit from collective experience. Community notes include sizing advice, quality assessments, and shipping tips.</p>
                      </div>
                    </div>
                  </section>

                  {/* Getting Started */}
                  <section id="getting-started">
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                      Getting Started: Your First Superbuy Spreadsheet
                    </h2>
                    <p className="text-sm md:text-base mb-4">
                      Ready to dive in? Getting started is surprisingly simple. Follow these five steps, and you will be browsing quality finds within minutes.
                    </p>
                    <div className="space-y-4 my-6">
                      {[
                        { step: '1', title: 'Visit the Hub', desc: 'Go to the Superbuy Spreadsheet Hub homepage where you will find 14 categories and the latest trending items. Everything is organized and easy to navigate.' },
                        { step: '2', title: 'Pick a Category', desc: 'Choose a category that interests you — Shoes is the most popular, but Hoodies, Jackets, and Accessories are also packed with great finds. Each category links directly to the relevant spreadsheet section.' },
                        { step: '3', title: 'Browse and Compare', desc: 'Scroll through the curated list. Look for items with high ratings and available QC photos. Compare similar items across different sellers to find the best combination of quality and price.' },
                        { step: '4', title: 'Open Product Links', desc: 'Click any product link to open the listing on Taobao, Weidian, or 1688. Review the seller\'s store rating, customer reviews, and available product photos.' },
                        { step: '5', title: 'Use an Agent to Purchase', desc: 'Copy the product link, paste it into your agent\'s search bar (Superbuy is recommended), add to cart, and check out. The agent handles everything from purchase to international shipping.' },
                      ].map((item) => (
                        <div key={item.step} className="flex gap-4">
                          <div className="w-8 h-8 rounded-full bg-accent-500 text-background-50 flex items-center justify-center font-bold text-sm flex-shrink-0 mt-0.5">
                            {item.step}
                          </div>
                          <div>
                            <h4 className="font-heading font-semibold text-foreground-900 text-sm">{item.title}</h4>
                            <p className="text-sm text-foreground-600 mt-0.5">{item.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm md:text-base">
                      Still feeling unsure? Head back to{' '}
                      <Link to="/" className="text-accent-600 hover:text-accent-700 underline font-medium">the Superbuy Spreadsheet Hub</Link>
                      {' '}and explore the categories at your own pace. The spreadsheet is designed to be intuitive — you do not need any prior experience to start finding quality products.
                    </p>
                  </section>

                  {/* Categories */}
                  <section id="categories">
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                      Understanding the 14 Core Categories
                    </h2>
                    <p className="text-sm md:text-base mb-4">
                      The Superbuy Spreadsheet organizes products into 14 distinct categories, making it easy to find exactly what you are looking for. Here is a breakdown of each category and what makes it special:
                    </p>
                    <div className="space-y-3 my-6">
                      {[
                        { name: 'Shoes', desc: 'The flagship category. Sneakers, boots, slides — everything from budget daily beaters to rare limited edition reps. The most active category with the most frequent updates.' },
                        { name: 'Hoodies / Sweaters', desc: 'Cozy streetwear essentials from brands like Stussy, Nike, Essentials, and more. Perfect for building a layered wardrobe without breaking the bank.' },
                        { name: 'T-Shirts', desc: 'Graphic tees, blank essentials, and designer-inspired pieces. Great starter items for first-time spreadsheet users because of their low price point and consistent quality.' },
                        { name: 'Jackets', desc: 'Outerwear for every season — lightweight windbreakers, puffer jackets, denim jackets, and technical shells from brands like Arc\'teryx and The North Face.' },
                        { name: 'Pants / Shorts', desc: 'Denim, cargo pants, sweatpants, and shorts. This category has grown significantly as more buyers discover the quality-to-price ratio of overseas pants.' },
                        { name: 'Headwear', desc: 'Caps, beanies, bucket hats, and more. A small but mighty category with some of the best brand-accurate accessories in the spreadsheet.' },
                        { name: 'Sets', desc: 'Coordinated outfits — tracksuits, two-piece sets, and matching collections. Great for buyers who want a complete look without mixing and matching.' },
                        { name: 'Underwear / Underpants', desc: 'The essentials you did not know you needed. Comfort-focused basics at prices that make restocking a no-brainer.' },
                        { name: 'Jersey', desc: 'Sports jerseys from NBA, NFL, soccer clubs, and more. One of the fastest-growing categories as international sports fans discover the spreadsheet.' },
                        { name: 'Accessories', desc: 'Watches, jewelry, phone cases, sunglasses, wallets — the catch-all for items that complete your look. Some of the best value finds live in this category.' },
                        { name: 'Bags', desc: 'Backpacks, messenger bags, totes, and crossbody bags. Surprising quality at every price point, from minimalist canvas to structured leather designs.' },
                        { name: 'Belts', desc: 'Designer-inspired belts and everyday essentials. A small but carefully curated selection that punches above its weight in terms of quality.' },
                        { name: 'Perfumes', desc: 'Fragrance finds that rival designer scents at a fraction of the cost. One of the most underrated categories — do not sleep on it.' },
                        { name: 'Other Stuff', desc: 'The wild card category. Electronics, home goods, stationery, and random treasures that do not fit elsewhere. Always worth a browse for unexpected gems.' },
                      ].map((cat) => (
                        <details key={cat.name} className="group bg-background-100 rounded-xl border border-background-200/60 overflow-hidden">
                          <summary className="flex items-center justify-between px-4 py-3 cursor-pointer list-none">
                            <span className="font-medium text-sm text-foreground-900">{cat.name}</span>
                            <span className="w-5 h-5 rounded-full bg-background-200 flex items-center justify-center flex-shrink-0 group-open:bg-accent-100 group-open:text-accent-600 transition-colors">
                              <i className="ri-add-line text-xs group-open:rotate-45 transition-transform"></i>
                            </span>
                          </summary>
                          <div className="px-4 pb-4 text-sm text-foreground-600 leading-relaxed">
                            {cat.desc}
                          </div>
                        </details>
                      ))}
                    </div>
                  </section>

                  {/* QC Photos */}
                  <section id="qc-photos">
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                      How to Read QC Photos Like a Seasoned Pro
                    </h2>
                    <p className="text-sm md:text-base mb-4">
                      QC (Quality Check) photos are arguably the most valuable feature of any Superbuy Spreadsheet. When you order through an agent, they will take photos of your items once they arrive at the warehouse. These photos let you inspect stitching, logos, colors, and overall build quality before the item ships internationally. If something looks off, you can request a return or exchange — saving you from receiving a disappointing product weeks later.
                    </p>
                    <div className="bg-background-100 rounded-xl border border-background-200/60 p-5 my-6">
                      <h4 className="font-heading font-semibold text-foreground-900 mb-3 text-sm">What to Look for in QC Photos:</h4>
                      <ul className="space-y-2 text-sm text-foreground-700">
                        <li className="flex items-start gap-2">
                          <i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                          <span><strong className="text-foreground-900">Logo placement and accuracy</strong> — check alignment, font, and stitching quality around logos</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                          <span><strong className="text-foreground-900">Stitching consistency</strong> — look for loose threads, uneven seams, or skipped stitches</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                          <span><strong className="text-foreground-900">Color accuracy</strong> — compare the QC photo to the seller\'s listing photos; lighting can trick you</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                          <span><strong className="text-foreground-900">Material texture</strong> — zoom in on the fabric; does it look like what you expected?</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                          <span><strong className="text-foreground-900">Measurements</strong> — many agents will include a measuring tape in the photo; verify against the size chart</span>
                        </li>
                      </ul>
                    </div>
                  </section>

                  {/* Comparison */}
                  <section id="comparison">
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                      Spreadsheet Shopping vs. Manual Searching — A Clear Winner
                    </h2>
                    <p className="text-sm md:text-base mb-4">
                      Let&apos;s put the numbers side by side so you can see exactly why spreadsheets have become the preferred method for international shoppers:
                    </p>
                    <div className="overflow-x-auto my-6">
                      <table className="w-full text-xs md:text-sm border border-background-200/60 rounded-xl overflow-hidden">
                        <thead>
                          <tr className="bg-background-100">
                            <th className="text-left py-3 px-4 font-semibold text-foreground-800">Feature</th>
                            <th className="text-left py-3 px-4 font-semibold text-accent-700">Superbuy Spreadsheet</th>
                            <th className="text-left py-3 px-4 font-semibold text-foreground-500">Manual Searching</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            ['Time to find 5 items', '10-15 minutes', '2-4 hours'],
                            ['Link reliability', '✅ Vetted & verified', '⚠️ Hit or miss'],
                            ['QC photos available', '✅ Yes, community-sourced', '❌ Only after purchase'],
                            ['Price comparison', '✅ Instant across sellers', '⚠️ Manual, time-consuming'],
                            ['Risk of scams', '⭐ Very low', '⭐⭐⭐ Moderate to high'],
                            ['Category organization', '✅ 14 structured categories', '❌ No organization'],
                            ['Community reviews', '✅ Real buyer feedback', '❌ None'],
                            ['Update frequency', '✅ Daily updates', '❌ N/A'],
                          ].map((row, i) => (
                            <tr key={i} className={i % 2 === 0 ? 'bg-background-50' : 'bg-background-100/50'}>
                              <td className="py-2.5 px-4 font-medium text-foreground-800">{row[0]}</td>
                              <td className="py-2.5 px-4 text-foreground-700">{row[1]}</td>
                              <td className="py-2.5 px-4 text-foreground-500">{row[2]}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>

                  {/* Pro Tips */}
                  <section id="tips">
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                      10 Pro Tips for Spreadsheet Shopping Success
                    </h2>
                    <div className="space-y-3 my-6">
                      {[
                        'Always check the QC photos section before ordering — community-sourced photos are more reliable than seller images.',
                        'Sort by rating when browsing. Items with 4+ stars and multiple reviews are almost always safe bets.',
                        'Compare at least 2-3 sellers for the same item. Prices can vary by 20-40% for identical products.',
                        'Pay attention to size charts. Asian sizing runs smaller than US/EU sizing in most cases.',
                        'Build a haul of 5+ items to maximize shipping efficiency. Consolidated shipping saves significantly on per-item costs.',
                        'Bookmark your favorite spreadsheet categories for quick access during future shopping sessions.',
                        'Check the "Last Updated" date — fresher spreadsheets have fewer dead links and more current pricing.',
                        'Use the "Other Stuff" category for discovering unique items you would never think to search for.',
                        'Join the community. Discord servers and Reddit threads often share exclusive finds before they hit the main spreadsheet.',
                        'Do not be afraid to GP (Guinea Pig) new finds. The best spreadsheet items often start as someone taking a chance on an unverified link.',
                      ].map((tip, i) => (
                        <div key={i} className="flex gap-3 bg-background-100 rounded-xl border border-background-200/60 p-4">
                          <span className="w-6 h-6 rounded-full bg-accent-500 text-background-50 flex items-center justify-center font-bold text-xs flex-shrink-0 mt-px">
                            {i + 1}
                          </span>
                          <p className="text-sm text-foreground-700 leading-relaxed">{tip}</p>
                        </div>
                      ))}
                    </div>
                  </section>

                  {/* Shipping */}
                  <section id="shipping">
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                      Shipping and Agent Guide — Getting Your Haul Home
                    </h2>
                    <p className="text-sm md:text-base mb-4">
                      Once you have found your items through the spreadsheet, the next step is getting them delivered. This is where a shopping agent becomes essential. Superbuy is the most popular choice, but alternatives like CSSBuy and AllChinaBuy also offer solid service. Here is how the process works:
                    </p>
                    <div className="space-y-3 my-6">
                      {[
                        { step: '1', title: 'Order Through Your Agent', desc: 'Paste the product link from the spreadsheet into your agent\'s search bar. Add the item to your cart and complete the purchase. The agent buys the item on your behalf.' },
                        { step: '2', title: 'Wait for Warehouse Arrival', desc: 'Most items arrive at the agent\'s warehouse within 3-7 days. You will receive a notification when your item has been received and logged.' },
                        { step: '3', title: 'Review QC Photos', desc: 'The agent takes detailed photos of your item. Review them carefully — check stitching, logos, colors, and measurements. Request additional photos if something is unclear.' },
                        { step: '4', title: 'Approve or Return', desc: 'Happy with the QC? Approve the item. Not satisfied? Most agents allow returns within a certain window. Some sellers charge a small restocking fee.' },
                        { step: '5', title: 'Build Your Haul', desc: 'Repeat the process for all your items. The agent stores everything in their warehouse (usually free for 30-90 days) until you are ready to ship.' },
                        { step: '6', title: 'Choose Shipping & Ship', desc: 'Select your preferred shipping line based on speed and cost. The agent consolidates your items, packages them, and sends them out. Track your package until it arrives at your doorstep.' },
                      ].map((item) => (
                        <div key={item.step} className="flex gap-4 p-4 bg-background-100 rounded-xl border border-background-200/60">
                          <div className="w-8 h-8 rounded-full bg-secondary-500 text-background-50 flex items-center justify-center font-bold text-sm flex-shrink-0 mt-0.5">
                            {item.step}
                          </div>
                          <div>
                            <h4 className="font-heading font-semibold text-foreground-900 text-sm">{item.title}</h4>
                            <p className="text-sm text-foreground-600 mt-0.5">{item.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>

                  {/* FAQ */}
                  <section id="faq">
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                      Frequently Asked Questions
                    </h2>
                    <div className="space-y-3">
                      {[
                        { q: 'Is the Superbuy Spreadsheet safe to use?', a: 'Absolutely. Every link in the spreadsheet has been vetted by experienced buyers. The spreadsheet itself does not handle any transactions — you purchase through your chosen agent, which adds an extra layer of security. Check the QC photos, read community reviews, and shop with confidence.' },
                        { q: 'How much can I save using a spreadsheet?', a: 'Savings vary by category, but most spreadsheet users report saving 40-70% compared to buying similar items from domestic retailers. Shoes and Hoodies typically offer the best value, while Accessories and Perfumes deliver outsized savings for their price point.' },
                        { q: 'Do I need to speak Chinese to use the spreadsheet?', a: 'Not at all! The spreadsheet is organized in English, and shopping agents like Superbuy provide fully English-language interfaces. Product names, categories, and descriptions are all in English. The entire experience is designed for international shoppers.' },
                        { q: 'How do I know which size to order?', a: 'Most spreadsheet entries include sizing notes from previous buyers. Pay attention to these — they often contain crucial information like "size up twice" or "fits true to US sizing." QC photos with measurements are your best friend for confirming sizes before shipping.' },
                        { q: 'What if an item arrives damaged or incorrect?', a: 'This is where QC photos shine. If you spot an issue during the QC stage, you can request a return or exchange before international shipping. Even after shipping, most agents offer some form of insurance or compensation for issues beyond your control.' },
                      ].map((faq, idx) => (
                        <details key={idx} className="group bg-background-100 rounded-xl border border-background-200/60 overflow-hidden">
                          <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                            <span className="font-medium text-sm text-foreground-900 pr-4">{faq.q}</span>
                            <span className="w-6 h-6 rounded-full bg-background-200 flex items-center justify-center flex-shrink-0 group-open:bg-accent-100 group-open:text-accent-600 transition-colors">
                              <i className="ri-add-line text-sm group-open:rotate-45 transition-transform"></i>
                            </span>
                          </summary>
                          <div className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">
                            {faq.a}
                          </div>
                        </details>
                      ))}
                    </div>
                  </section>

                  {/* CTA */}
                  <div className="bg-accent-500 rounded-2xl p-6 md:p-8 text-center mt-10">
                    <h3 className="font-heading text-xl md:text-2xl font-bold text-background-50 mb-3">
                      Ready to Start Your First Spreadsheet Haul?
                    </h3>
                    <p className="text-sm md:text-base text-accent-100 mb-6 max-w-lg mx-auto">
                      Head back to the Superbuy Spreadsheet Hub and explore 14 curated categories with thousands of verified links. Your next favorite find is just a click away.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                      <Link
                        to="/"
                        className="w-full sm:w-auto px-7 py-3 bg-background-50 text-accent-700 font-semibold text-sm rounded-full hover:bg-background-100 transition-all hover:scale-105 whitespace-nowrap"
                      >
                        Back to Hub
                        <i className="ri-arrow-left-line ml-2"></i>
                      </Link>
                      <a
                        href="https://kaspreadsheet.com"
                        target="_blank"
                        rel="noopener nofollow"
                        className="w-full sm:w-auto px-7 py-3 border-2 border-background-50/40 text-background-50 font-semibold text-sm rounded-full hover:bg-background-50/10 transition-all whitespace-nowrap cursor-pointer"
                      >
                        Superbuy Sheet
                        <i className="ri-arrow-right-up-line ml-2"></i>
                      </a>
                    </div>
                  </div>

                  {/* Related Links */}
                  <div className="border-t border-background-200/60 pt-8 mt-10">
                    <h4 className="font-heading font-semibold text-foreground-900 mb-3 text-sm">Continue Exploring:</h4>
                    <div className="flex flex-wrap gap-2">
                      <Link to="/" className="px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-600 hover:bg-accent-50 hover:text-accent-600 transition-colors">
                        ← Superbuy Spreadsheet Hub
                      </Link>
                      <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-600 hover:bg-accent-50 hover:text-accent-600 transition-colors cursor-pointer">
                        Latest Spreadsheet Finds →
                      </a>
                      <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-600 hover:bg-accent-50 hover:text-accent-600 transition-colors cursor-pointer">
                        Shop All Categories →
                      </a>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}